import React, { useEffect, useState } from "react";
import Register from "./Register";
import { X, User, Lock, HardHat, Building2, Shield, Wrench, Eye, EyeOff, ArrowLeft, Mail, Phone, } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";
import BgImage from "../../Assets/Images/hero1.webp";

const UserLogin = ({ isModalOpen = true }) => {
  const [isOpen, setIsOpen] = useState(isModalOpen);
  const [currentView, setCurrentView] = useState("login");
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    remember: false,
  });
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: "",
    phoneNumber: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [focusedField, setFocusedField] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const rememberedCredentials = localStorage.getItem("rememberedCredentials");
    const rememberedToken = localStorage.getItem("userToken");
    const rememberedUser = localStorage.getItem("userData");

    if (rememberedCredentials) {
      try {
        const credentials = JSON.parse(rememberedCredentials);
        setFormData((prev) => ({
          ...prev,
          username: credentials.username || "",
          password: credentials.password || "",
          remember: true,
        }));
      } catch (error) {
        localStorage.removeItem("rememberedCredentials");
      }
    }

    if (rememberedToken && rememberedUser) {
      try {
        const userData = JSON.parse(rememberedUser);
        toast.success("Welcome back! Auto-logging you in...");
        setTimeout(() => {
          navigate("/");
        }, 1000);
      } catch (error) {
        localStorage.removeItem("userToken");
        localStorage.removeItem("userData");
      }
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleForgotPasswordChange = (e) => {
    const { name, value } = e.target;
    setForgotPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.username || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);

    try {
      const apiData = {
        identifier: formData.username,
        password: formData.password,
      };

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/users/login`,
        headers: {
          "Content-Type": "application/json",
        },
        data: apiData,
      };

      const response = await axios.request(config);

      console.log("Login successful:", response.data);
      toast.success("Login successful!");

      if (response.data.token && response.data.user) {
        localStorage.setItem("userToken", response.data.token);
        localStorage.setItem("userData", JSON.stringify(response.data.user));

        sessionStorage.removeItem("userToken");
        sessionStorage.removeItem("userData");
      }

      if (formData.remember) {
        const credentialsToSave = {
          username: formData.username,
          password: formData.password,
        };
        localStorage.setItem(
          "rememberedCredentials",
          JSON.stringify(credentialsToSave)
        );
        toast.success("Login credentials saved for next time!");
      } else {
        localStorage.removeItem("rememberedCredentials");
      }

      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (error) {
      console.error("Login error:", error);

      if (error.response) {
        const errorMessage = error.response.data?.message || "Login failed";
        toast.error(errorMessage);

        if (error.response.status === 401) {
          toast.error(
            "Invalid credentials. Please check your email/phone and password."
          );
        } else if (error.response.status === 404) {
          toast.error("User not found. Please check your credentials.");
        } else if (error.response.status >= 500) {
          toast.error("Server error. Please try again later.");
        }
      } else if (error.request) {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async () => {
    if (
      !forgotPasswordData.email ||
      !forgotPasswordData.phoneNumber ||
      !forgotPasswordData.newPassword ||
      !forgotPasswordData.confirmPassword
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (forgotPasswordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);

    try {
      const apiData = {
        email: forgotPasswordData.email,
        phoneNumber: forgotPasswordData.phoneNumber,
        newPassword: forgotPasswordData.newPassword,
      };

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/users/forgot-password`,
        headers: {
          "Content-Type": "application/json",
        },
        data: apiData,
      };

      const response = await axios.request(config);

      console.log("Password reset successful:", response.data);
      toast.success(
        "Password reset successful! You can now login with your new password."
      );

      setForgotPasswordData({
        email: "",
        phoneNumber: "",
        newPassword: "",
        confirmPassword: "",
      });
      setCurrentView("login");
    } catch (error) {
      console.error("Password reset error:", error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message || "Password reset failed";
        toast.error(errorMessage);

        if (error.response.status === 404) {
          toast.error(
            "Email or phone number not found. Please check your details."
          );
        } else if (error.response.status >= 500) {
          toast.error("Server error. Please try again later.");
        }
      } else if (error.request) {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (currentView === "login") {
        handleSubmit();
      } else {
        handleForgotPasswordSubmit();
      }
    }
  };

  const clearRememberedData = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("rememberedCredentials");
    sessionStorage.removeItem("userToken");
    sessionStorage.removeItem("userData");
    setFormData((prev) => ({
      ...prev,
      username: "",
      password: "",
      remember: false,
    }));
    toast.success("All saved data cleared");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-900 to-amber-900 flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        backgroundImage: `
            radial-gradient(circle at center, rgba(0,0,0,0) 60%, rgba(0,0,0,0.3) 100%),
            url(${BgImage})
          `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundBlendMode: "darken",
      }}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse delay-500"></div>

        <div className="absolute top-20 left-20 text-orange-300 opacity-10 animate-bounce">
          <HardHat className="w-12 h-12" />
        </div>
        <div className="absolute top-40 right-32 text-amber-300 opacity-10 animate-bounce delay-700">
          <Wrench className="w-10 h-10" />
        </div>
        <div className="absolute bottom-32 left-32 text-yellow-300 opacity-10 animate-bounce delay-300">
          <Building2 className="w-14 h-14" />
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
          {/* Glassmorphic Card */}
          <div className="relative z-10 w-full max-w-lg p-8 rounded-2xl shadow-2xl bg-white/5 backdrop-blur-3xl border border-white/20 animate-fade-in transform transition-all duration-500">
            {/* Header with close and back buttons */}
            <div className="relative mb-6">
              {currentView === "forgot" && (
                <button
                  onClick={() => setCurrentView("login")}
                  className="absolute left-0 top-0 text-white/70 hover:text-orange-300 transition-colors duration-200"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
              )}
              <h2 
                className="text-3xl font-bold text-center bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent drop-shadow-lg"
                style={{ WebkitTextStroke: "1px rgba(255,255,255,0.3)" }}
              >
                {currentView === "login" ? "User Login" : "Reset Password"}
              </h2>
            </div>

            {currentView === "login" ? (
              <>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">
                    Welcome Back!
                  </h3>
                  <p className="text-white/70">
                    Enter your credentials to continue
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white">
                      Email ID / Phone Number
                    </label>
                    <div className="relative">
                      <User
                        className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-all duration-300 ${
                          focusedField === "username" ? "text-orange-400" : "text-white/50"
                        }`}
                      />
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        onFocus={() => setFocusedField("username")}
                        onBlur={() => setFocusedField("")}
                        onKeyPress={handleKeyPress}
                        className="w-full pl-12 pr-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all duration-300"
                        placeholder="Enter Your Email ID / Phone Number"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white">
                      Password
                    </label>
                    <div className="relative">
                      <Lock
                        className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-all duration-300 ${
                          focusedField === "password" ? "text-orange-400" : "text-white/50"
                        }`}
                      />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        onFocus={() => setFocusedField("password")}
                        onBlur={() => setFocusedField("")}
                        onKeyPress={handleKeyPress}
                        className="w-full pl-12 pr-12 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all duration-300"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-orange-400 transition-colors duration-200"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <label className="flex items-center group cursor-pointer">
                      <input
                        type="checkbox"
                        name="remember"
                        checked={formData.remember}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-orange-600 bg-white/20 border-2 border-white/30 rounded focus:ring-orange-500 focus:ring-2 transition-all duration-200"
                      />
                      <span className="ml-2 text-sm text-white/80 group-hover:text-white transition-colors">
                        Remember me
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setCurrentView("forgot")}
                      className="text-sm text-orange-300 hover:text-orange-200 font-medium transition-all duration-200 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90 text-white py-3 rounded-lg font-semibold transition duration-300 flex items-center justify-center shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Authenticating...
                      </>
                    ) : (
                      <>
                        <HardHat className="w-5 h-5 mr-2" />
                        Login
                      </>
                    )}
                  </button>

                  <p className="text-sm text-white/80 text-center">
                    Don't have an account?{" "}
                    <span
                      className="text-orange-300 cursor-pointer hover:underline"
                      onClick={() => {
                        setIsRegisterOpen(true);
                        setIsOpen(false);
                      }}
                    >
                      Register here
                    </span>
                  </p>

                  {(localStorage.getItem("rememberedCredentials") ||
                    localStorage.getItem("userData")) && (
                    <button
                      onClick={clearRememberedData}
                      className="w-full text-sm text-white/60 hover:text-white/80 transition-colors duration-200 mt-4"
                    >
                      Clear all saved data
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">
                    Reset Your Password
                  </h3>
                  <p className="text-white/70">
                    Enter your email, phone number and new password
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail
                        className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-all duration-300 ${
                          focusedField === "email" ? "text-orange-400" : "text-white/50"
                        }`}
                      />
                      <input
                        type="email"
                        name="email"
                        value={forgotPasswordData.email}
                        onChange={handleForgotPasswordChange}
                        onFocus={() => setFocusedField("email")}
                        onBlur={() => setFocusedField("")}
                        onKeyPress={handleKeyPress}
                        className="w-full pl-12 pr-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all duration-300"
                        placeholder="Enter your email address"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone
                        className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-all duration-300 ${
                          focusedField === "phoneNumber" ? "text-orange-400" : "text-white/50"
                        }`}
                      />
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={forgotPasswordData.phoneNumber}
                        onChange={handleForgotPasswordChange}
                        onFocus={() => setFocusedField("phoneNumber")}
                        onBlur={() => setFocusedField("")}
                        onKeyPress={handleKeyPress}
                        className="w-full pl-12 pr-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all duration-300"
                        placeholder="Enter your phone number"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock
                        className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-all duration-300 ${
                          focusedField === "newPassword" ? "text-orange-400" : "text-white/50"
                        }`}
                      />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        name="newPassword"
                        value={forgotPasswordData.newPassword}
                        onChange={handleForgotPasswordChange}
                        onFocus={() => setFocusedField("newPassword")}
                        onBlur={() => setFocusedField("")}
                        onKeyPress={handleKeyPress}
                        className="w-full pl-12 pr-12 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all duration-300"
                        placeholder="Enter your new password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-orange-400 transition-colors duration-200"
                      >
                        {showNewPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Lock
                        className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-all duration-300 ${
                          focusedField === "confirmPassword" ? "text-orange-400" : "text-white/50"
                        }`}
                      />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={forgotPasswordData.confirmPassword}
                        onChange={handleForgotPasswordChange}
                        onFocus={() => setFocusedField("confirmPassword")}
                        onBlur={() => setFocusedField("")}
                        onKeyPress={handleKeyPress}
                        className="w-full pl-12 pr-12 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all duration-300"
                        placeholder="Confirm your new password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-orange-400 transition-colors duration-200"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {forgotPasswordData.newPassword &&
                      forgotPasswordData.confirmPassword &&
                      forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword && (
                        <p className="text-red-400 text-sm mt-1">
                          Passwords do not match
                        </p>
                      )}
                  </div>

                  <button
                    onClick={handleForgotPasswordSubmit}
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90 text-white py-3 rounded-lg font-semibold transition duration-300 flex items-center justify-center shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Resetting Password...
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5 mr-2" />
                        Reset Password
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <Register
        isOpen={isRegisterOpen}
        onClose={() => {
          setIsRegisterOpen(false);
          setIsOpen(true);
        }}
      />
    </div>
  );
};

export default UserLogin;